import { Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { Mxa920DeviceUiDeviceBehaviorPlugin } from '@shure/device-ui/shared/device-behavior';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceModel, MeasurementUnit } from '@shure/shared/angular/data-access/system-api/models';
import { CoverageImage, CoverageUnitService } from '@shure/shared/angular/features/device-coverage/ui';

@Injectable()
export class Mxa920CdmDeviceBehaviorPlugin extends Mxa920DeviceUiDeviceBehaviorPlugin implements CoverageUnitService {
	public readonly unit = signal(MeasurementUnit.Feet);

	private readonly ASSETS_PATH = 'assets/images/devices';

	constructor(protected override readonly transloco: TranslocoService) {
		super(transloco);
	}

	public override getCoverageImage(model: DeviceModel): CoverageImage {
		return {
			live: `${this.ASSETS_PATH}/device-${model.toLowerCase()}-back.svg`,
			virtual: `${this.ASSETS_PATH}/device-${model.toLowerCase()}-back-line.svg`,
			isImageRound: model === DeviceModel.MXA920R ? true : false
		};
	}

	public override getDefaultRoute(): DeviceUiRoute {
		return DeviceUiRoute.Coverage;
	}

	public override getAvailablePages$(
		_model: DeviceModel,
		options?:
			| {
					virtual?: boolean | undefined;
					autoCoverageMode?: boolean | undefined;
					dspSettingsPageEnabled?: boolean;
			  }
			| undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.coverage'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.intellimix'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.dsp-settings')
		]).pipe(
			map(([coverageLabel, channelsLabel, intellimixLabel, presetsLabel, settingsLabel, dspSettingsLabel]) => {
				let pages = [
					{
						route: DeviceUiRoute.Coverage,
						label: coverageLabel
					},
					{
						route: DeviceUiRoute.Channels,
						label: channelsLabel
					},
					{
						route: DeviceUiRoute.Intellimix,
						label: intellimixLabel
					},
					{
						route: DeviceUiRoute.Presets,
						label: presetsLabel
					},
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					},
					{
						route: DeviceUiRoute.DspSettings,
						label: dspSettingsLabel
					}
				];

				if (options?.autoCoverageMode) {
					pages = pages.filter((p) => p.route !== DeviceUiRoute.Channels);
				}

				if (options && options.dspSettingsPageEnabled !== true) {
					pages = pages.filter((p) => p.route !== DeviceUiRoute.DspSettings);
				}

				return pages;
			})
		);
	}
}
